import { css } from "@emotion/react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { Flex, IDBadge, Text } from "@phoenix/components";
import { LatencyText } from "@phoenix/components/trace/LatencyText";
import { SpanKindToken } from "@phoenix/components/trace/SpanKindToken";
import { SpanStatusCodeIcon } from "@phoenix/components/trace/SpanStatusCodeIcon";
import { SpanTokenCosts } from "@phoenix/components/trace/SpanTokenCosts";
import { SpanTokenCount } from "@phoenix/components/trace/SpanTokenCount";
import { useTimeFormatters } from "@phoenix/hooks";

import { deriveSpanIdentity } from "./spanHeaderUtils";

import type { SpanHeader_span$key } from "./__generated__/SpanHeader_span.graphql";

type SpanHeaderProps = {
  span: SpanHeader_span$key;
};
export function SpanHeader(props: SpanHeaderProps) {
  const { fullTimeFormatter } = useTimeFormatters();
  const span = useFragment(
    graphql`
      fragment SpanHeader_span on Span {
        id
        name
        spanKind
        spanId
        code: statusCode
        latencyMs
        startTime
        tokenCountTotal
        attributes
        costSummary {
          total {
            cost
          }
        }
      }
    `,
    props.span
  );

  const startTime = useMemo<Date>(() => {
    return new Date(span.startTime);
  }, [span.startTime]);

  // `deriveSpanIdentity` always returns `string | undefined` for both fields —
  // never an object — so these are safe to render directly. `undefined` means
  // "no value present"; the corresponding chip is omitted rather than showing a
  // fabricated placeholder. See spanHeaderUtils.ts for the key precedence.
  const { agentName, userName } = useMemo(
    () => deriveSpanIdentity(span.attributes),
    [span.attributes]
  );

  return (
    <Flex
      direction="row"
      gap="size-100"
      width="100%"
      height="100%"
      alignItems="center"
    >
      <Flex direction="column" gap="size-50">
        <Flex direction="row" gap="size-100" alignItems="center">
          <SpanKindToken spanKind={span.spanKind} />
          <Text size="L" css={css`font-weight: 850; font-size: 16px; color: #000000;`}>{span.name}</Text>
          <SpanStatusCodeIcon
            statusCode={span.code}
            css={css`
              font-size: var(--global-font-size-m);
              flex-shrink: 0;
            `}
          />
          {agentName ? (
            <span
              title={`agent name: ${agentName}`}
              css={css`
                background: #f1f5f9;
                color: #0f172a;
                font-weight: 850;
                font-size: 13px;
                padding: 3px 12px;
                border-radius: 14px;
                border: 1px solid #cbd5e1;
                display: inline-flex;
                align-items: center;
                max-width: 220px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {agentName}
            </span>
          ) : null}
          {userName ? (
            <span
              title={`user: ${userName}`}
              css={css`
                background: #f1f5f9;
                color: #0f172a;
                font-weight: 850;
                font-size: 13px;
                padding: 3px 12px;
                border-radius: 14px;
                border: 1px solid #cbd5e1;
                display: inline-flex;
                align-items: center;
                max-width: 180px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              user: {userName}
            </span>
          ) : null}
        </Flex>
        <Flex direction="row" gap="size-100" alignItems="center">
          <IDBadge id={span.spanId} />
          {typeof span.latencyMs === "number" ? (
            <LatencyText latencyMs={span.latencyMs} size="S" />
          ) : null}
          <Text color="text-500" size="S">
            at {fullTimeFormatter(startTime)}
          </Text>
          {span.tokenCountTotal ? (
            <SpanTokenCount
              tokenCountTotal={span.tokenCountTotal}
              nodeId={span.id}
              size="S"
            />
          ) : null}
          {span.costSummary?.total?.cost ? (
            <SpanTokenCosts
              totalCost={span.costSummary.total.cost}
              spanNodeId={span.id}
              size="S"
            />
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  );
}
